import { useEffect } from "react";
import { useSWRConfig } from "swr";

/**
 * bfcache（back/forward cache）復元時の SWR エラー回復。
 *
 * フルページ遷移（外部リンクや apps/content への素の <a> 等）でページを離れる瞬間、
 * 進行中の fetch が中断されて SWR にエラーが記録され、そのまま bfcache に凍結されることがある。
 * グローバル設定で revalidateOnFocus / revalidateOnReconnect を無効化しているため、
 * 戻るボタンで復元されたページはエラー状態のまま自己回復しない（凍結された「読み込みエラー」
 * 画面が表示され続ける）。pageshow (persisted=true) は bfcache 復元の瞬間だけ発火するので、
 * そこでエラー状態のキーに限定して再検証する。正常なキーは触らないため、
 * revalidateOnFocus を切っている意図（不要な再取得を増やさない）は維持される。
 */
export const useBfcacheRecovery = () => {
  const { cache, mutate } = useSWRConfig();

  useEffect(() => {
    const revalidateErroredKeys = () => {
      for (const key of cache.keys()) {
        if (cache.get(key)?.error !== undefined) {
          mutate(key);
        }
      }
    };

    let timerId: number | undefined;
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      revalidateErroredKeys();
      // 遷移時に中断された fetch の reject が「復元後」に処理されるケース
      // （凍結時点ではまだエラー未記録）を拾うための遅延再スキャン
      timerId = window.setTimeout(revalidateErroredKeys, 500);
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      window.clearTimeout(timerId);
    };
  }, [cache, mutate]);
};
